/**
 * Image Optimization Utilities
 * Generates optimized image URLs using Cloudflare's image resizing
 */

/**
 * Generate a Cloudflare Image Resizing URL
 * @param {string} imageUrl - Original image URL
 * @param {object} options - Resize options
 * @returns {string} Optimized image URL
 */
export function getOptimizedImageUrl(imageUrl, options = {}) {
  if (!imageUrl) return '';

  const {
    width = null,
    height = null,
    quality = 85,
    format = 'auto', // auto, webp, avif, json
    fit = 'cover', // scale-down, contain, cover, crop, pad
  } = options;

  // Check if it's a Cloudflare R2/CDN URL
  const isCloudflareUrl = imageUrl.includes('.r2.dev') || imageUrl.includes('.cloudflare.com');

  if (!isCloudflareUrl) {
    // Not a Cloudflare URL, return original
    return imageUrl;
  }

  // Build Cloudflare Image Resizing URL
  // Format: /cdn-cgi/image/width=400,quality=85,format=auto/https://example.com/image.jpg
  const params = [];

  if (width) params.push(`width=${width}`);
  if (height) params.push(`height=${height}`);
  if (quality) params.push(`quality=${quality}`);
  if (format) params.push(`format=${format}`);
  if (fit) params.push(`fit=${fit}`);

  const paramsString = params.join(',');

  // Extract the domain from the original URL
  const url = new URL(imageUrl);
  const cdnPath = `/cdn-cgi/image/${paramsString}${url.pathname}`;

  return `${url.origin}${cdnPath}`;
}

/**
 * Get thumbnail version of image (small, for product cards)
 */
export function getThumbnail(imageUrl) {
  return getOptimizedImageUrl(imageUrl, {
    width: 400,
    quality: 80,
    format: 'webp',
    fit: 'cover'
  });
}

/**
 * Get medium version of image (for product detail page)
 */
export function getMediumImage(imageUrl) {
  return getOptimizedImageUrl(imageUrl, {
    width: 800,
    quality: 85,
    format: 'webp',
    fit: 'cover'
  });
}

/**
 * Get large version of image (for zoom/lightbox)
 */
export function getLargeImage(imageUrl) {
  return getOptimizedImageUrl(imageUrl, {
    width: 1200,
    quality: 90,
    format: 'webp',
    fit: 'cover'
  });
}

/**
 * Get tiny blur placeholder (very small for blur-up effect)
 */
export function getBlurPlaceholder(imageUrl) {
  return getOptimizedImageUrl(imageUrl, {
    width: 20,
    quality: 50,
    format: 'webp',
    fit: 'cover'
  });
}

/**
 * Generate srcset for responsive images
 */
export function generateSrcSet(imageUrl, sizes = [400, 800, 1200]) {
  return sizes
    .map(size => `${getOptimizedImageUrl(imageUrl, { width: size, quality: 85, format: 'webp' })} ${size}w`)
    .join(', ');
}
